/** The app talks only to its own API (/api/*); never to Graph directly. */
import type { ApiError } from '@shared/api-types';
import { BUSY_DELAYS_MS, busyEnded, busyStarted, clientLog, isBusyAnswer } from './busy';
import { notePress } from './press-track';
import { honeypotValue } from './honeypot';

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError | null,
  ) {
    super(body?.message ?? `Request failed (${status})`);
    this.name = 'ApiRequestError';
  }
}

const wait = (ms: number) => new Promise<void>((r) => globalThis.setTimeout(r, ms));

/** Every request the app sends. A press (anything but a read) is noted for the button being pressed (D-1023-BUSY). */
export function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const p = send<T>(path, init);
  if ((init.method ?? 'GET').toUpperCase() !== 'GET') notePress(p, path.startsWith('/api/transactions/'));
  return p;
}

/** 1102 (B3): a public page's POST carries what a script put in the hidden field, as `website` (empty for every person: nothing is added). */
function withHidden(path: string, method: string, init: RequestInit): RequestInit {
  const hidden = honeypotValue();
  if (!hidden || method !== 'POST' || !path.startsWith('/api/link/') || typeof init.body !== 'string') return init;
  try {
    const body = JSON.parse(init.body) as unknown;
    return body && typeof body === 'object' && !Array.isArray(body) ? { ...init, body: JSON.stringify({ ...(body as Record<string, unknown>), website: hidden }) } : init;
  } catch {
    return init;
  }
}

async function send<T>(path: string, given: RequestInit): Promise<T> {
  const method = (given.method ?? 'GET').toUpperCase();
  const init = withHidden(path, method, given);
  // Reads are tried again by themselves while Microsoft is busy; a press (POST/PATCH) is never re-sent.
  const tries = method === 'GET' ? BUSY_DELAYS_MS.length : 0;
  let busy = false;
  try {
    for (let attempt = 0; ; attempt++) {
      const res = await fetch(path, {
        ...init,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...(init.headers ?? {}) },
      });
      const type = res.headers.get('content-type') ?? '';
      const body = type.includes('application/json') ? ((await res.json()) as unknown) : null;
      if (!res.ok) {
        const err = (body as ApiError | null) ?? null;
        if (isBusyAnswer(res.status, err?.error) && attempt < tries) {
          if (!busy) busyStarted();
          busy = true;
          clientLog('info', 'api.busy', `${method} ${path.split('?')[0]} → ${res.status}; trying again`);
          await wait(BUSY_DELAYS_MS[attempt]!);
          continue;
        }
        clientLog('error', 'api.error', `${method} ${path.split('?')[0]} → ${res.status} ${err?.error ?? ''} ${err?.message ?? ''}`);
        throw new ApiRequestError(res.status, err);
      }
      if (body === null) throw new ApiRequestError(res.status, { error: 'not-json', message: 'The server did not answer with data (are you signed in?).' });
      return body as T;
    }
  } finally {
    if (busy) busyEnded();
  }
}
